/**
 * 日志管理 API 服务
 * 封装统一日志管理接口（通过 Agent Admin 聚合层）
 */

import { ApiClient, apiClients } from '@/shared/services/api'

// ==================== 类型定义 ====================

/** 服务日志配置信息 */
export interface ServiceInfo {
  service: string
  log_file: string | null
  current_level: string
  file_exists: boolean
  file_size: number
  reachable: boolean
  error?: string
}

/** 日志条目 */
export interface LogEntry {
  timestamp: string
  level: string
  module: string
  function: string
  line: number
  message: string
  request_id: string | null
}

/** 日志查询结果 */
export interface LogQueryResult {
  logs: LogEntry[]
  total: number
  has_more: boolean
  source: string
  /** 原始文件行数 */
  raw_lines?: number
  /** 多行合并后的条目数 */
  merged_entries?: number
  /** 成功解析的条目数 */
  parsed_entries?: number
  error?: string
  debug?: string
}

/** 跨服务追踪日志条目 */
export interface TraceLogEntry extends LogEntry {
  service: string
}

/** 请求追踪结果 */
export interface TraceResult {
  request_id: string
  logs: TraceLogEntry[]
  services: string[]
  total: number
  errors: Array<{ service: string; error: string }>
}

/** 日志级别变更结果 */
export interface LevelChangeResult {
  service: string
  old_level: string
  new_level: string
  persistent: boolean
}

/** 日志查询参数 */
export interface LogQueryParams {
  service: string
  level?: string
  search?: string
  request_id?: string
  lines?: number
  offset?: number
}

// ==================== 服务键名 ====================

export const SERVICE_KEYS = [
  { key: 'axiom-iam', label: 'IAM', port: 8000 },
  { key: 'axiom-agent-admin', label: 'Agent Admin', port: 8001 },
  { key: 'axiom-agent-runtime', label: 'Agent Runtime', port: 8002 },
  { key: 'axiom-memory-admin', label: 'Memory Admin', port: 8003 },
  { key: 'axiom-memory-runtime', label: 'Memory Runtime', port: 8004 },
  { key: 'axiom-identity-engine', label: 'Identity Engine', port: 8090 },
  { key: 'axiom-mcp', label: 'MCP', port: 8006 },
] as const

export type ServiceKey = typeof SERVICE_KEYS[number]['key']

// ==================== 日志级别 ====================

export const LOG_LEVELS = ['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'] as const
export type LogLevel = typeof LOG_LEVELS[number]

// 日志级别颜色映射
export const LOG_LEVEL_COLORS: Record<string, string> = {
  DEBUG: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  INFO: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  WARNING: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  ERROR: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  CRITICAL: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200',
}

// ==================== API 服务 ====================

export const logApi = {
  /**
   * 获取所有服务列表和日志状态
   * GET /api/v1/log-aggregate/services
   */
  async getServices(): Promise<ServiceInfo[]> {
    const response = await ApiClient.getWithClient<ServiceInfo[]>(
      apiClients.agentAdmin,
      '/log-aggregate/services',
    )
    return response.data
  },

  /**
   * 查询指定服务的日志
   * GET /api/v1/log-aggregate/query
   */
  async queryLogs(params: LogQueryParams): Promise<LogQueryResult> {
    const response = await ApiClient.getWithClient<LogQueryResult>(
      apiClients.agentAdmin,
      '/log-aggregate/query',
      { params },
    )
    return response.data
  },

  /**
   * 跨服务请求追踪
   * GET /api/v1/log-aggregate/trace/{request_id}
   */
  async traceRequest(requestId: string): Promise<TraceResult> {
    const response = await ApiClient.getWithClient<TraceResult>(
      apiClients.agentAdmin,
      `/log-aggregate/trace/${requestId}`,
    )
    return response.data
  },

  /**
   * 动态设置指定服务的日志级别
   * POST /api/v1/log-aggregate/level
   */
  async setLogLevel(service: string, level: string): Promise<LevelChangeResult> {
    const response = await ApiClient.postWithClient<LevelChangeResult>(
      apiClients.agentAdmin,
      '/log-aggregate/level',
      null,
      { params: { service, level } },
    )
    return response.data
  },
}
